import { Fragment, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addExpenseTag, updateExpenseTag, deleteExpenseTag, getExpenseTags, getAllExpenseTags } from "../../services/TransactionService";
import { PERMISSION_SLUGS, ROLE_SLUGS } from "../../utils/auth/permissionConstants";
import useGuidedTour from "../../hooks/useGuidedTour";
import { usePartnerHasSoloUser } from "../../hooks/usePartnerHasSoloUser";
import {
  ADMIN_DETAILS_SUPER_ADMIN_SETUP_TOUR_ID,
  buildAdminDetailsSuperAdminSetupTourSteps,
} from "./PartnerDetails.tour";
import { getAllPartnerTeams, getActivePartnerUserCount } from "../../services/PartnerService";
import { SECURITY_OUTRO_TOUR_ID } from "../../hooks/faqOutroSteps";

import { useSelector } from "react-redux";

import { fetchPartnerDetails } from "../../services/PartnerService";
import PartnerUsersSection from "./PartnerUsersSection";
import PartnerTeamsSection from "./PartnerTeamsSection";
import SoloOrganizationBanner from "./SoloOrganizationBanner";
import { useNavigate, useParams } from "react-router-dom";
import { canManagePartnerDetails } from "../../utils/auth/accessRules";

const SUGGESTED_EXPENSE_TAGS = ['Rent', 'Lunch', 'Transport', 'Airtime', 'Stationery'];

const suggestedTagsDismissKeyFor = (partnerId) => `expenseTagSuggestions:dismissed:${partnerId}`;

const readSuggestedTagsDismissed = (partnerId) => {
  if (!partnerId) return false;
  try {
    return window.localStorage.getItem(suggestedTagsDismissKeyFor(partnerId)) === '1';
  } catch {
    return false;
  }
};

const writeSuggestedTagsDismissed = (partnerId) => {
  if (!partnerId) return;
  try {
    window.localStorage.setItem(suggestedTagsDismissKeyFor(partnerId), '1');
  } catch {
    // localStorage may be blocked; the row reappearing next visit is fine.
  }
};

function buildPageWindows(currentPage, lastPage) {
  const pages = new Set([1, lastPage]);
  for (let i = currentPage - 2; i <= currentPage + 2; i++) {
    if (i >= 1 && i <= lastPage) pages.add(i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  let prev = 0;
  for (const page of sorted) {
    if (page - prev > 1) result.push('...');
    result.push(page);
    prev = page;
  }
  return result;
}

const PartnerDetails = (props) => {
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [expenseTagToRemove, setExpenseTagToRemove] = useState(null);
  const [viewAddExpenseTagModal, setViewAddExpenseTagModal] = useState(false);
  const [isSubmittingExpenseTagForm, setIsSubmittingExpenseTagForm] = useState(false);
  const [isSubmittingExpenseTagDeleteForm, setIsSubmittingExpenseTagDeleteForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [editTagName, setEditTagName] = useState('');
  const [isSubmittingExpenseTagEditForm, setIsSubmittingExpenseTagEditForm] = useState(false);
  const usersCardRef = useRef(null);
  const teamsCardRef = useRef(null);
  const [expenseTagsCurrentPage, setExpenseTagsCurrentPage] = useState(1);
  const expenseTagsCardRef = useRef(null);

  const goToExpenseTagsPage = (nextPage) => {
    setExpenseTagsCurrentPage(nextPage);
    expenseTagsCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { authUser } = useSelector(state => state.auth);
  const { id: paramPartnerId } = useParams();
  const partnerId = paramPartnerId || authUser?.partner_id;

  const { hasSoloUser, isLoading: hasSoloUserLoading } = usePartnerHasSoloUser(partnerId);

  const { data: partnerDetailsData, isLoading: loading, isError: error } = useQuery({
    queryKey: ['partnerDetails', partnerId],
    queryFn: () => fetchPartnerDetails(partnerId),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
  });
  const userPartnerDetails = partnerDetailsData?.data?.data ?? null;

  const {
    data: expenseTagsData,
    isLoading: expenseTagsLoading,
    isFetching: expenseTagsFetching,
  } = useQuery({
    queryKey: ['expenseTags', partnerId, expenseTagsCurrentPage],
    queryFn: () => getExpenseTags(partnerId, expenseTagsCurrentPage),
    placeholderData: (previousData) => previousData,
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
  });
  const expenseTags = expenseTagsData?.data?.data ?? null;
  const expenseTagsMeta = expenseTagsData?.data?.meta ?? null;

  const { data: allExpenseTagsData } = useQuery({
    queryKey: ['allExpenseTags', partnerId],
    queryFn: () => getAllExpenseTags(partnerId),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
  });
  const allExpenseTagsList = allExpenseTagsData?.data?.data ?? [];
  const existingTagNamesLower = new Set(
    allExpenseTagsList.map((tag) => (tag?.name ?? '').trim().toLowerCase())
  );
  const availableSuggestedTags = SUGGESTED_EXPENSE_TAGS.filter(
    (name) => !existingTagNamesLower.has(name.toLowerCase())
  );
  const [pendingSuggestedTags, setPendingSuggestedTags] = useState(() => new Set());
  const [suggestedTagsDismissed, setSuggestedTagsDismissed] = useState(() => readSuggestedTagsDismissed(partnerId));
  useEffect(() => {
    setSuggestedTagsDismissed(readSuggestedTagsDismissed(partnerId));
  }, [partnerId]);
  const handleDismissSuggestedTags = () => {
    writeSuggestedTagsDismissed(partnerId);
    setSuggestedTagsDismissed(true);
  };

  const isSuperAdmin = authUser?.role_slug === ROLE_SLUGS.SUPER_ADMIN;
  const hasAnyExpenseTag = Array.isArray(expenseTags) && expenseTags.length > 0;

  const { data: setupTeamsData, isLoading: setupTeamsLoading } = useQuery({
    queryKey: ['allPartnerTeamsForSetupNudge', partnerId],
    queryFn: () => getAllPartnerTeams(partnerId),
    enabled: !!partnerId && isSuperAdmin,
    staleTime: 60 * 1000,
  });
  const { data: setupActiveUsersCount, isLoading: setupActiveUsersCountLoading } = useQuery({
    queryKey: ['activePartnerUsersCountForSetupNudge', partnerId],
    queryFn: () => getActivePartnerUserCount(partnerId).then(res => res.data.countActiveUsers),
    enabled: !!partnerId && isSuperAdmin,
    staleTime: 60 * 1000,
  });

  const setupTourDataReady =
    isSuperAdmin && !expenseTagsLoading && !setupTeamsLoading && !setupActiveUsersCountLoading && !hasSoloUserLoading;
  const hasAnyTeam = (setupTeamsData?.data?.data?.length ?? 0) > 0;
  const hasAnyActiveUser = (setupActiveUsersCount ?? 0) > 0;
  const partnerNeedsSetup = !hasAnyExpenseTag || !hasAnyTeam || !hasAnyActiveUser;

  useGuidedTour(
    ADMIN_DETAILS_SUPER_ADMIN_SETUP_TOUR_ID,
    buildAdminDetailsSuperAdminSetupTourSteps({
      navigate,
      hasTransactionPin: authUser?.has_transaction_pin,
      completedTourIds: authUser?.completed_tour_ids ?? [],
      partnerNeedsSetup,
      hasSoloUser,
    }),
    {
      enabled: isSuperAdmin && setupTourDataReady,
      alsoMarkCompleted: [SECURITY_OUTRO_TOUR_ID],
    }
  );

  const [expenseTagFormData, setExpenseTagFormData] = useState({
    expenseTagName: "",
  });

  const handleExpenseTagInputChange = (e) => {
    const { id, value } = e.target;
    setExpenseTagFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleAddExpenseTagData = async (e) => {
    e.preventDefault();

    if (isSubmittingExpenseTagForm) return;
    setIsSubmittingExpenseTagForm(true);

    const payload = {
      name: expenseTagFormData.expenseTagName,
    };

    try {
      await addExpenseTagMutation.mutateAsync(payload);
      setExpenseTagsCurrentPage(1);
      queryClient.invalidateQueries({ queryKey: ['expenseTags', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['allExpenseTags', partnerId] });
      setViewAddExpenseTagModal(false);
      toast.success("Expense tag added successfully!");
      resetAddExpenseTagForm();
      setIsSubmittingExpenseTagForm(false);
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to add expense tag. Try again later.");
      setIsSubmittingExpenseTagForm(false);
    }
  };

  const resetAddExpenseTagForm = () => {
    setExpenseTagFormData({
      expenseTagName: "",
    });
  };

  const addExpenseTagMutation = useMutation({
    mutationFn: (data) => addExpenseTag(partnerId, data),
  });

  const handleAddSuggestedTag = async (name) => {
    if (pendingSuggestedTags.has(name)) return;
    setPendingSuggestedTags((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    try {
      await addExpenseTagMutation.mutateAsync({ name });
      setExpenseTagsCurrentPage(1);
      queryClient.invalidateQueries({ queryKey: ['expenseTags', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['allExpenseTags', partnerId] });
      toast.success(`${name} added to expense tags.`);
    } catch (err) {
      toast.error(err.response?.data?.error ?? `Could not add ${name}. Try again.`);
    } finally {
      setPendingSuggestedTags((prev) => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }
  };

  const updateExpenseTagMutation = useMutation({
    mutationFn: ({ id, data }) => updateExpenseTag(partnerId, id, data),
  });

  const deleteExpenseTagMutation = useMutation({
    mutationFn: (expenseTagId) => deleteExpenseTag(partnerId, expenseTagId),
  });

  const handleEditExpenseTag = async (e) => {
    e.preventDefault();
    if (isSubmittingExpenseTagEditForm) return;
    setIsSubmittingExpenseTagEditForm(true);

    const editAction = async () => {
      await updateExpenseTagMutation.mutateAsync({ id: editingTag.id, data: { name: editTagName } });
      queryClient.invalidateQueries({ queryKey: ['expenseTags', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['allExpenseTags', partnerId] });
      setEditingTag(null);
      setEditTagName('');
    };

    toast.promise(
      editAction(),
      {
        pending: 'Saving...',
        success: 'Expense tag updated.',
        error: {
          render({ data: err }) {
            return err?.response?.data?.error || 'Failed to update expense tag.';
          }
        }
      }
    ).finally(() => {
      setIsSubmittingExpenseTagEditForm(false);
    });
  };

  const handleRemoveExpenseTag = async (expenseTagId) => {
    if (isSubmittingExpenseTagDeleteForm) return;
    setIsSubmittingExpenseTagDeleteForm(true);

    const deleteAction = async () => {
      await deleteExpenseTagMutation.mutateAsync(expenseTagId);
      queryClient.invalidateQueries({ queryKey: ['expenseTags', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['allExpenseTags', partnerId] });
    };

    toast.promise(
        deleteAction(),
        {
          pending: 'Deleting tag...',
          success: 'Expense tag deleted.',
          error: {
            render({ data: err }) {
              if (err?.response?.data?.error_code === 'EXPENSE_TAG_IS_SYSTEM') {
                return 'This tag is managed by Milipot and cannot be deleted. You can rename it if you prefer a different label.';
              }
              return err?.response?.data?.message
                || err?.response?.data?.error
                || 'Failed to delete the expense tag.';
            }
          }
        }
    ).finally(() => {
      setIsSubmittingExpenseTagDeleteForm(false);
    });
  };

  useEffect(() => {
    setExpenseTagsCurrentPage(1);
  }, [partnerId]);

  useEffect(() => {
    if (!userPartnerDetails) return;
    const hash = window.location.hash;
    if (hash !== '#expense-tags' && hash !== '#teams') return;
    const target = document.getElementById(hash.slice(1));
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [userPartnerDetails]);

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center py-5">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading…</span>
      </div>
    </div>
  );
  if (error) return (
    <div className="d-flex justify-content-center py-5">
      <p className="text-danger">Failed to load partner details.</p>
    </div>
  );
  if (!userPartnerDetails) return null;

  return (
    <Fragment>
      <SoloOrganizationBanner show={isSuperAdmin && hasSoloUser} />
      <div className="row">
        <div className="col-12">
          <div className="card overflow-hidden">
            <div
              className="text-center p-3 overlay-box "
              style={{ backgroundImage: `url()` }}
            >
              <h3 className="mt-3 mb-1 text-white">{userPartnerDetails.name}</h3>
            </div>
            <ul className="list-group list-group-flush">
              <li className="list-group-item d-flex justify-content-between">
                <span className="mb-0">Status</span>{" "}
                <strong className="text-muted text-capitalize">{userPartnerDetails.status}</strong>
              </li>
              <li className="list-group-item d-flex justify-content-between">
                <span className="mb-0">Created On</span>{" "}
                <strong className="text-muted">{userPartnerDetails.created_when}</strong>
              </li>
            </ul>
          </div>
        </div>
        <div className="col-xl-12 col-lg-12 col-xxl-12 col-sm-12" id="expense-tags">
          <div className="card" ref={expenseTagsCardRef} style={{ scrollMarginTop: '5rem' }} data-tour="admin-details-expense-tags">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Expense Tags</h4>
              {canManagePartnerDetails(authUser, userPartnerDetails, PERMISSION_SLUGS.MANAGE_EXPENSE_TAGS) && (
                <>
                  <button
                    type="button"
                    className="btn btn-primary d-none d-lg-inline-flex"
                    onClick={() => setViewAddExpenseTagModal(true)}
                    data-tour="admin-details-add-expense-tag-button"
                  >
                    Add Expense Tag
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary rounded-circle d-inline-flex d-lg-none align-items-center justify-content-center shadow-sm"
                    style={{ width: '2.5rem', height: '2.5rem', padding: 0 }}
                    title="Add expense tag"
                    aria-label="Add expense tag"
                    onClick={() => setViewAddExpenseTagModal(true)}
                    data-tour="admin-details-add-expense-tag-button"
                  >
                    <i className="fas fa-plus" aria-hidden="true" />
                  </button>
                </>
              )}
            </div>
            {canManagePartnerDetails(authUser, userPartnerDetails, PERMISSION_SLUGS.MANAGE_EXPENSE_TAGS)
              && availableSuggestedTags.length > 0
              && !suggestedTagsDismissed && (
              <div
                className="mx-3 mt-3 p-3 rounded-3"
                style={{
                  backgroundColor: 'rgba(120, 82, 250, 0.06)',
                  border: '1px solid rgba(120, 82, 250, 0.18)',
                }}
                data-tour="admin-details-suggested-expense-tags"
              >
                <div className="d-flex justify-content-between align-items-start mb-3 gap-2">
                  <div>
                    <div className="fw-semibold text-body d-flex align-items-center gap-2">
                      <i className="fas fa-lightbulb text-primary" aria-hidden="true" />
                      Suggested tags
                    </div>
                    <div className="text-muted small mt-1">
                      Common categories to get you started. Click one to add it.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none text-muted p-0"
                    onClick={handleDismissSuggestedTags}
                    aria-label="Dismiss suggested expense tags"
                  >
                    Dismiss
                  </button>
                </div>
                <div className="d-flex flex-wrap" style={{ gap: '0.5rem' }}>
                  {availableSuggestedTags.map((name) => {
                    const isPending = pendingSuggestedTags.has(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        className="btn btn-outline-primary btn-sm rounded-pill px-3 shadow-sm"
                        style={{ backgroundColor: '#ffffff' }}
                        onClick={() => handleAddSuggestedTag(name)}
                        disabled={isPending}
                        aria-label={`Add suggested expense tag ${name}`}
                      >
                        {isPending ? (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        ) : (
                          <i className="fas fa-plus me-2" aria-hidden="true" />
                        )}
                        {name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="card-body p-0" style={{ position: 'relative' }}>
              {expenseTagsFetching && expenseTags && expenseTags.length > 0 && (
                <div style={{
                  position: 'absolute', inset: 0, zIndex: 10,
                  backgroundColor: 'rgba(255,255,255,0.6)',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                  paddingTop: '1rem',
                }}>
                  <span className="spinner-border spinner-border-sm text-primary" role="status" />
                </div>
              )}
              {expenseTagsLoading ? (
                <div className="d-flex justify-content-center py-4">
                  <span className="spinner-border spinner-border-sm text-primary" role="status" />
                </div>
              ) : expenseTags && expenseTags.length > 0 ? (
                <ul className="list-group list-group-flush">
                  {expenseTags.map((tag) => (
                    <li
                      key={tag.id}
                      className="list-group-item d-flex justify-content-between align-items-center gap-3 flex-nowrap py-3"
                    >
                      <span
                        className="fw-semibold text-body flex-grow-1"
                        style={{ minWidth: 0, overflowWrap: 'anywhere' }}
                      >
                        {tag.name}
                      </span>
                      {canManagePartnerDetails(authUser, userPartnerDetails, PERMISSION_SLUGS.MANAGE_EXPENSE_TAGS) && (
                        <div className="d-inline-flex flex-shrink-0 gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            title="Edit expense tag"
                            aria-label={`Edit ${tag.name}`}
                            onClick={() => {
                              setEditingTag(tag);
                              setEditTagName(tag.name);
                            }}
                          >
                            <i className="fas fa-pencil-alt" aria-hidden="true" />
                          </button>
                          {!tag.is_system && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              title="Delete expense tag"
                              aria-label={`Delete ${tag.name}`}
                              onClick={() => {
                                setExpenseTagToRemove(tag);
                                setShowRemoveModal(true);
                              }}
                            >
                              <i className="fa fa-trash" aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-center text-muted py-4 mb-0">No expense tags.</p>
              )}
              {expenseTagsMeta && expenseTagsMeta.last_page > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-3 py-3 border-top">
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none p-0"
                    onClick={() => goToExpenseTagsPage(expenseTagsMeta.current_page - 1)}
                    disabled={expenseTagsMeta.current_page <= 1}
                    aria-label="Previous page"
                  >
                    <i className="fas fa-chevron-left" aria-hidden="true" />
                  </button>
                  <span className="small text-muted">
                    Page {expenseTagsMeta.current_page} of {expenseTagsMeta.last_page}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none p-0"
                    onClick={() => goToExpenseTagsPage(expenseTagsMeta.current_page + 1)}
                    disabled={expenseTagsMeta.current_page >= expenseTagsMeta.last_page}
                    aria-label="Next page"
                  >
                    <i className="fas fa-chevron-right" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {!hasSoloUser && (
          <div className="col-xl-12 col-lg-12 col-xxl-12 col-sm-12" id="teams">
            <PartnerTeamsSection
              partnerId={partnerId}
              authUser={authUser}
              userPartnerDetails={userPartnerDetails}
              cardRef={teamsCardRef}
              mode="preview"
              onSeeAll={() => navigate("/teams")}
              onAddExpenseTag={() => {
                expenseTagsCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                setViewAddExpenseTagModal(true);
              }}
            />
          </div>
        )}
        <div className="col-xl-12 col-lg-12 col-xxl-12 col-sm-12">
          <PartnerUsersSection
            partnerId={partnerId}
            authUser={authUser}
            userPartnerDetails={userPartnerDetails}
            cardRef={usersCardRef}
            mode="preview"
            onSeeAll={() => navigate("/users")}
            onAddExpenseTag={() => {
              expenseTagsCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              setViewAddExpenseTagModal(true);
            }}
            onAddTeam={() => {
              teamsCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          />
        </div>
      </div>


      <Modal show={viewAddExpenseTagModal} onHide={setViewAddExpenseTagModal} className="modal fade"
             id="viewAddExpenseTagModal">
        <>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Expense Tag</h5>
              <button
                type="button"
                onClick={() => setViewAddExpenseTagModal(false)}
                className="btn-close"
                data-dismiss="modal"
              >
              </button>
            </div>
            <div className="modal-body">
              <form
                onSubmit={handleAddExpenseTagData}
              >
                <label
                  className="form-check-label"
                  htmlFor="expenseTagName"
                >
                  Name
                </label>
                <div className="form-group mb-3">
                  <input
                    type="text"
                    className="form-control input-default "
                    placeholder=""
                    id="expenseTagName"
                    value={expenseTagFormData.expenseTagName || ""}
                    onChange={handleExpenseTagInputChange}
                  />
                </div>

                <button
                  className="btn btn-primary btn-block"
                  disabled={!expenseTagFormData.expenseTagName}
                >{`${isSubmittingExpenseTagForm ? "Processing..." : "Add"}`}
                </button>
              </form>
            </div>
          </div>
        </>
      </Modal>


      <Modal show={!!editingTag} onHide={() => { setEditingTag(null); setEditTagName(''); }} centered>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Expense Tag</h5>
            <button type="button" className="btn-close" onClick={() => { setEditingTag(null); setEditTagName(''); }} />
          </div>
          <div className="modal-body">
            <form onSubmit={handleEditExpenseTag}>
              <label className="form-check-label" htmlFor="editTagName">Name</label>
              <div className="form-group mb-3">
                <input
                  type="text"
                  className="form-control input-default"
                  id="editTagName"
                  value={editTagName}
                  onChange={(e) => setEditTagName(e.target.value)}
                  disabled={isSubmittingExpenseTagEditForm}
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={isSubmittingExpenseTagEditForm || !editTagName.trim() || editTagName === editingTag?.name}
              >
                {isSubmittingExpenseTagEditForm ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Save'}
              </button>
            </form>
          </div>
        </div>
      </Modal>

      <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Removal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove <strong>{expenseTagToRemove?.name}</strong> from the expense tags list?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              handleRemoveExpenseTag(expenseTagToRemove.id);
              setShowRemoveModal(false);
            }}
          >
            Remove
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default PartnerDetails;